// C.1 — батч-2: rare/epic/legendary юниты Лив Лайфа из v6 xlsx.
//
// Сгруппировано по типу триггера. Каждый юнит — отдельный кейс. Все
// триггеры регистрируются в соответствующих реестрах (healing observer,
// EOT, SOT, resurrect, attack, damage, summoned, died).

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { healUnit, registerOnAllyHealedObserver, registerOnHealTrigger } from '../heal.js';
import { applyPermanentBuff, applyTemporaryBuff, removePermanentBuffsBySource } from '../../../buffs.js';
import { applyDamage } from '../../../combat.js';
import { resolveTarget } from '../../../effects/targets.js';
import { getHealTarget } from '../../../heal-priority.js';
import { addInfectedStack } from '../../../keywords/infected.js';
import { registerOnAttackTrigger } from '../on-attack-triggers.js';
import { registerOnDamageTakenTrigger } from '../on-damage-triggers.js';
import { registerOnEndOfTurnTrigger } from '../eot-triggers.js';
import { registerOnStartOfTurnTrigger } from '../start-of-turn-triggers.js';
import { registerPostResurrectHook } from '../resurrect.js';
import { resurrectUnit } from '../resurrect.js';
import {
  registerOnUnitDiedExtraHandler,
  registerUnitSummonedExtraHandler
} from '../../deus/hooks.js';
import { applyPosthumousContract } from '../../../keywords/posthumous_contract.js';
import { Card } from '../../../card.js';
import { registerC1Order } from '../../global/c1/order-registry.js';

// ============================================================
// === Группа: TRIGGER on ALLY HEALED (+self perm buff)       ===
// ============================================================

// === Авангард Vita (livlife_avangard_vita, rare) ===
// Триггер ally_healed: +1/0/0 self perm. Лимит +4 за матч.
const AVANGARD_VITA_ID = 'livlife_avangard_vita';
registerOnAllyHealedObserver(AVANGARD_VITA_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== AVANGARD_VITA_ID) return;
  if (observer.current?.silenced) return;
  if ((healEvent?.amount || 0) <= 0) return;
  const cnt = observer.avangardVitaStacks || 0;
  if (cnt >= 4) return;
  applyPermanentBuff(state, observer, { atk: 1, armor: 0, hp: 0 }, AVANGARD_VITA_ID, 'Авангард Vita');
  observer.avangardVitaStacks = cnt + 1;
});
registerImplementedAbility(AVANGARD_VITA_ID, 'trigger');

// === Гипер-хирург «Бойцовый» (livlife_giper_hirurg_boytsovy, legendary) ===
// Триггер ally_healed: +0/0/1 self perm, без лимита.
const GIPER_BOYTSOVY_ID = 'livlife_giper_hirurg_boytsovy';
registerOnAllyHealedObserver(GIPER_BOYTSOVY_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== GIPER_BOYTSOVY_ID) return;
  if (observer.current?.silenced) return;
  if ((healEvent?.amount || 0) <= 0) return;
  applyPermanentBuff(state, observer, { atk: 0, armor: 0, hp: 1 }, GIPER_BOYTSOVY_ID, 'Гипер-хирург');
});
registerImplementedAbility(GIPER_BOYTSOVY_ID, 'trigger');

// === Капсула боя (livlife_kapsula_boya, epic) ===
// Аура (через ally_healed observer): другому случайному союзнику +1/0/0
// до конца следующего хода.
const KAPSULA_BOYA_ID = 'livlife_kapsula_boya';
registerOnAllyHealedObserver(KAPSULA_BOYA_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== KAPSULA_BOYA_ID) return;
  if (observer.current?.silenced) return;
  if ((healEvent?.amount || 0) <= 0) return;
  const owner = state.players?.[observer.ownerId];
  if (!owner?.field) return;
  // Случайный союзник кроме самого Капсулы боя и кроме healed-цели.
  const candidates = [];
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u === observer || u === healEvent.target) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.type !== 'unit') continue;
    candidates.push(u);
  }
  if (candidates.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(candidates.length) : Math.floor(Math.random() * candidates.length);
  const pick = candidates[idx];
  const expiresAtTurn = (state.currentTurn ?? 1) + 2;
  applyTemporaryBuff(state, pick, { atk: 1, armor: 0, hp: 0 }, expiresAtTurn, KAPSULA_BOYA_ID, 'Капсула боя');
});
registerImplementedAbility(KAPSULA_BOYA_ID, 'aura');

// === Псих (livlife_psikh, legendary) ===
// Триггер ally_healed: 3 урона случайному вражескому персонажу (юниту/Герою),
// 1 раз за ход.
const PSIKH_ID = 'livlife_psikh';
registerOnAllyHealedObserver(PSIKH_ID, (state, observer, healEvent) => {
  if (!observer || observer.id !== PSIKH_ID) return;
  if (observer.current?.silenced) return;
  if ((healEvent?.amount || 0) <= 0) return;
  const turn = state.currentTurn ?? 0;
  if (observer.psikhLastFiredTurn === turn) return;
  const enemyIdx = 1 - observer.ownerId;
  const enemy = state.players?.[enemyIdx];
  if (!enemy) return;
  const targets = [];
  if (enemy.field) for (const row of enemy.field) for (const u of row || []) {
    if (u && (!u.isAlive || u.isAlive())) targets.push(u);
  }
  if (enemy.hero?.hp > 0) targets.push(enemy.hero);
  if (targets.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(targets.length) : Math.floor(Math.random() * targets.length);
  applyDamage(targets[idx], 3, { state, source: { id: PSIKH_ID, name_ru: 'Псих' } });
  observer.psikhLastFiredTurn = turn;
});
registerImplementedAbility(PSIKH_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on SELF HEALED (+self perm buff)       ===
// ============================================================

// === Аугмент «Стимпак» (livlife_augment_stimpak, rare) ===
// Триггер unit_takes_heal filter=self: +1/0/0 self perm.
const AUGMENT_STIMPAK_ID = 'livlife_augment_stimpak';
registerOnHealTrigger(AUGMENT_STIMPAK_ID, (state, healEvent) => {
  const target = healEvent?.target;
  if (!target || target.id !== AUGMENT_STIMPAK_ID) return;
  if ((healEvent.amount || 0) <= 0) return;
  applyPermanentBuff(state, target, { atk: 1, armor: 0, hp: 0 }, AUGMENT_STIMPAK_ID, 'Аугмент Стимпак');
});
registerImplementedAbility(AUGMENT_STIMPAK_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on SELF TAKES DAMAGE                    ===
// ============================================================

// === Био-имплант LL-9 (livlife_bio_implant_ll9, rare) ===
// Триггер unit_takes_damage filter=self: +1/0/0 self perm.
const BIO_IMPLANT_LL9_ID = 'livlife_bio_implant_ll9';
registerOnDamageTakenTrigger(BIO_IMPLANT_LL9_ID, (state, target, damageToHp) => {
  if (!state?.players) return;
  if (damageToHp <= 0) return;
  applyPermanentBuff(state, target, { atk: 1, armor: 0, hp: 0 }, BIO_IMPLANT_LL9_ID, 'Био-имплант LL-9');
});
registerImplementedAbility(BIO_IMPLANT_LL9_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on UNIT_ATTACK filter=self             ===
// ============================================================

// === Хирург-инвазивный «Скальпель» (livlife_hirurg_skalpel, rare) ===
// Триггер: heal 1 self при атаке.
const HIRURG_SKALPEL_ID = 'livlife_hirurg_skalpel';
registerOnAttackTrigger(HIRURG_SKALPEL_ID, (state, attacker) => {
  if (!state?.players) return;
  if (!attacker || attacker.id !== HIRURG_SKALPEL_ID) return;
  if (attacker.current?.silenced) return;
  healUnit(state, attacker, 1, attacker);
});
registerImplementedAbility(HIRURG_SKALPEL_ID, 'trigger');

// === Хирург-агрессор «Острие» (livlife_hirurg_agressor_ostrie, epic) ===
// Триггер unit_attack filter=self: heal 3 ally_lowest_hp.
const HIRURG_OSTRIE_ID = 'livlife_hirurg_agressor_ostrie';
registerOnAttackTrigger(HIRURG_OSTRIE_ID, (state, attacker) => {
  if (!state?.players) return;
  if (!attacker || attacker.id !== HIRURG_OSTRIE_ID) return;
  if (attacker.current?.silenced) return;
  const targets = resolveTarget(state, attacker, 'ally_lowest_hp');
  if (targets.length > 0) healUnit(state, targets[0], 3, attacker);
});
registerImplementedAbility(HIRURG_OSTRIE_ID, 'trigger');

// === Био-палач (livlife_bio_palach, rare) ===
// Триггер unit_attack filter=killed_target: heal 2 self.
const BIO_PALACH_ID = 'livlife_bio_palach';
registerOnAttackTrigger(BIO_PALACH_ID, (state, attacker, target) => {
  if (!state?.players) return;
  if (!attacker || attacker.id !== BIO_PALACH_ID) return;
  if (attacker.current?.silenced) return;
  // Цель должна быть мертва.
  if (!target) return;
  if (target.type === 'unit' && target.isAlive && target.isAlive()) return;
  if (target.type === 'hero' && (target.hp || 0) > 0) return;
  healUnit(state, attacker, 2, attacker);
});
registerImplementedAbility(BIO_PALACH_ID, 'trigger');

// === Доктор «Чума» (livlife_doctor_chuma, rare) ===
// Триггер unit_attack: +1 урон по заражённым целям, накладывает 1 стак с каждым ударом.
// Реализация: после нанесения base-урона — если target ещё жив, добиваем +1 если был заражён,
// а потом накладываем 1 стак (если target всё ещё жив).
const DOCTOR_CHUMA_ID = 'livlife_doctor_chuma';
registerOnAttackTrigger(DOCTOR_CHUMA_ID, (state, attacker, target) => {
  if (!state?.players) return;
  if (!attacker || attacker.id !== DOCTOR_CHUMA_ID) return;
  if (attacker.current?.silenced) return;
  if (!target) return;
  // Только по юнитам (Герой не заражается).
  if (target.type !== 'unit') return;
  const wasInfected = (target.infectedStacks || 0) > 0;
  // Цель ещё жива?
  if (target.isAlive && target.isAlive()) {
    if (wasInfected) {
      applyDamage(target, 1, { state, source: { id: DOCTOR_CHUMA_ID, name_ru: 'Доктор Чума: +1 vs Заражённого' } });
    }
    // Цель ещё жива?
    if (target.isAlive && target.isAlive()) {
      addInfectedStack(state, target, DOCTOR_CHUMA_ID, 1);
    }
  }
});
registerImplementedAbility(DOCTOR_CHUMA_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on ALLY DIED filter=same_line          ===
// ============================================================

// === Дрон Lazaro (livlife_dron_lazaro, common) ===
// Триггер: при смерти союзника на той же линии — heal 1 lowest_hp.
const DRON_LAZARO_ID = 'livlife_dron_lazaro';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  const ownerIdx = payload?.ownerIdx;
  if (!dead || ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  // Линия мёртвого: payload.line или dead.lastLine.
  const deadLine = payload.line ?? dead.lastLine ?? dead.line;
  if (deadLine == null) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== DRON_LAZARO_ID) continue;
    if (u.current?.silenced) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.line !== deadLine) continue;
    const targets = resolveTarget(state, u, 'ally_lowest_hp');
    if (targets.length > 0) healUnit(state, targets[0], 1, u);
  }
});
registerImplementedAbility(DRON_LAZARO_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on ENEMY DIED filter=infected          ===
// ============================================================

// === Главный эпидемиолог (livlife_glavny_epidemiolog, epic) ===
// Триггер enemy_died filter=died_while_infected: +1/0/1 случайному союзнику.
const EPIDEMIOLOG_ID = 'livlife_glavny_epidemiolog';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead) return;
  // died_while_infected — стак был > 0 в момент смерти.
  if ((dead.infectedStacks || 0) <= 0) return;
  const enemyOwnerIdx = payload?.ownerIdx;
  if (enemyOwnerIdx == null) return;
  // Эпидемиолог принадлежит ПРОТИВОПОЛОЖНОЙ стороне (для него enemy = эта).
  const livOwnerIdx = 1 - enemyOwnerIdx;
  const owner = state.players?.[livOwnerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const epi of row || []) {
    if (!epi || epi.id !== EPIDEMIOLOG_ID) continue;
    if (epi.current?.silenced) continue;
    if (!epi.isAlive || !epi.isAlive()) continue;
    // Выбрать случайного союзника Эпидемиолога.
    const allies = [];
    for (const r2 of owner.field) for (const u of r2 || []) {
      if (u && u.isAlive && u.isAlive()) allies.push(u);
    }
    if (allies.length === 0) continue;
    const idx = state.rng?.int ? state.rng.int(allies.length) : Math.floor(Math.random() * allies.length);
    applyPermanentBuff(state, allies[idx], { atk: 1, armor: 0, hp: 1 }, EPIDEMIOLOG_ID, 'Эпидемиолог');
  }
});
registerImplementedAbility(EPIDEMIOLOG_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on ALLY RESURRECTED (post-resurrect)   ===
// ============================================================

// === Капельница (livlife_kapelnitsa, rare) ===
// Триггер ally_resurrected: всем союзным юнитам +0/1/0 perm.
const KAPELNITSA_ID = 'livlife_kapelnitsa';

// === Доктор «Регенус» (livlife_doctor_regenus, rare) ===
// Триггер ally_resurrected: +1/0/1 self perm.
const DOCTOR_REGENUS_ID = 'livlife_doctor_regenus';

// === Лаборант «Феникс» (livlife_laborant_feniks, epic) ===
// Триггер ally_resurrected: воскрешённый юнит получает +1/1/1.
const LABORANT_FENIKS_ID = 'livlife_laborant_feniks';

// === Хирург-генетик «Исхак» (livlife_hirurg_genetik_iskhak, epic) ===
// Аура: все воскрешённые союзники получают keyword Посмертный контракт.
const ISKHAK_ID = 'livlife_hirurg_genetik_iskhak';

registerPostResurrectHook((state, resurrected, ownerIdx) => {
  if (!resurrected || ownerIdx == null) return;
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u.current?.silenced) continue;

    // Капельница — +0/1/0 ВСЕМ союзникам.
    if (u.id === KAPELNITSA_ID) {
      // Один проход по всем союзникам.
      for (const r2 of owner.field) for (const t of r2 || []) {
        if (!t || !t.isAlive || !t.isAlive()) continue;
        applyPermanentBuff(state, t, { atk: 0, armor: 1, hp: 0 }, KAPELNITSA_ID, 'Капельница');
      }
    }
    // Доктор Регенус — +1/0/1 себе.
    if (u.id === DOCTOR_REGENUS_ID) {
      applyPermanentBuff(state, u, { atk: 1, armor: 0, hp: 1 }, DOCTOR_REGENUS_ID, 'Доктор Регенус');
    }
    // Лаборант Феникс — +1/1/1 воскрешённому.
    if (u.id === LABORANT_FENIKS_ID && resurrected !== u) {
      applyPermanentBuff(state, resurrected, { atk: 1, armor: 1, hp: 1 }, LABORANT_FENIKS_ID, 'Лаборант Феникс');
    }
    // Исхак — даёт Посмертный контракт воскрешённому. Перманентно даже после смерти Исхака.
    if (u.id === ISKHAK_ID && resurrected !== u) {
      applyPosthumousContract(state, resurrected, ISKHAK_ID, 'Исхак');
    }
  }
});
registerImplementedAbility(KAPELNITSA_ID, 'trigger');
registerImplementedAbility(DOCTOR_REGENUS_ID, 'trigger');
registerImplementedAbility(LABORANT_FENIKS_ID, 'trigger');
registerImplementedAbility(ISKHAK_ID, 'aura');

// ============================================================
// === Группа: TRIGGER on TURN_START                          ===
// ============================================================

// === Прима-генетик «Лазурная» (livlife_prima_genetik_lazurnaya, legendary) ===
// Триггер turn_start: воскресить случайного юнита из сброса с full hp.
const PRIMA_LAZURNAYA_ID = 'livlife_prima_genetik_lazurnaya';
registerOnStartOfTurnTrigger(PRIMA_LAZURNAYA_ID, (state, source, ownerIdx) => {
  if (!source || source.id !== PRIMA_LAZURNAYA_ID) return;
  if (source.current?.silenced) return;
  const owner = state.players?.[ownerIdx];
  if (!owner) return;
  const discardUnits = (owner.discard || []).filter(c => c?.type === 'unit');
  if (discardUnits.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(discardUnits.length) : Math.floor(Math.random() * discardUnits.length);
  resurrectUnit(state, discardUnits[idx], ownerIdx, null, { hpMode: 'full_hp' });
});
registerImplementedAbility(PRIMA_LAZURNAYA_ID, 'trigger');

// ============================================================
// === Группа: TRIGGER on TURN_END                            ===
// ============================================================

// === Главный медик «Лазарь» (livlife_glavny_medik_lazar, epic) ===
// Триггер turn_end: heal 3 по priority.
// Order (composite): return_to_hand самого раненого + Лазарь на его ячейку.
const LAZAR_ID = 'livlife_glavny_medik_lazar';
registerOnEndOfTurnTrigger(LAZAR_ID, (state, source) => {
  if (!source || source.id !== LAZAR_ID) return;
  if (source.current?.silenced) return;
  const t = getHealTarget(state, source);
  if (t) healUnit(state, t, 3, source);
});
registerImplementedAbility(LAZAR_ID, 'trigger');

// Order Лазаря — composite.
import { evacuateUnitToHand } from '../evacuate.js';
registerC1Order(LAZAR_ID, (state, source) => {
  if (!source) return { ok: false };
  // Найти самого раненого союзника (кроме Лазаря).
  const owner = state.players?.[source.ownerId];
  if (!owner?.field) return { ok: false };
  let wounded = null;
  let minHpRatio = 1;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u === source) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    const cur = u.current?.hp ?? 0;
    const max = u.base?.hp ?? cur;
    if (max <= 0) continue;
    const ratio = cur / max;
    if (ratio < minHpRatio) { minHpRatio = ratio; wounded = u; }
  }
  if (!wounded) return { ok: true, reason: 'no_wounded' };
  // Запоминаем старую ячейку Лазаря.
  const lazarLine = source.line, lazarSlot = source.slot;
  const freedLine = wounded.line, freedSlot = wounded.slot;
  // Эвакуация раненого (full restore через evacuateUnitToHand).
  evacuateUnitToHand(state, source.ownerId, wounded, 'Лазарь');
  // Перемещаем Лазаря на освобождённую ячейку.
  if (owner.field[freedLine]?.[freedSlot] === null
      && owner.field[lazarLine]?.[lazarSlot] === source) {
    owner.field[lazarLine][lazarSlot] = null;
    owner.field[freedLine][freedSlot] = source;
    source.line = freedLine;
    source.slot = freedSlot;
    state.log?.add(`🩺 Лазарь → л.${freedLine + 1} сл.${freedSlot + 1}`);
  }
  return { ok: true };
});
registerImplementedAbility(LAZAR_ID, 'order');

// === Лекарь LL-4 — триггер «возврат карты в руку» УБРАН (редизайн фаундера) ===
// Теперь у Лекаря только turn_end-heal 3 (см. units-batch.js). Доп. триггер
// card_returned_to_hand больше не используется.

// ============================================================
// === Группа: TRIGGER on ALLY SUMMONED filter=from_hand     ===
// ============================================================

// === Координатор Vita (livlife_koordinator_vita, rare) ===
// Триггер ally_summoned filter=from_hand: heal 3 по priority.
// Реализация: используем общий хук registerUnitSummonedExtraHandler.
const KOORDINATOR_VITA_ID = 'livlife_koordinator_vita';
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  if (!card) return;
  // Был ли это призыв из руки или из сброса (резурект)?
  // resurrectUnit устанавливает posthumousContractUsed/initiation=0 — но не флаг.
  // Простой эвристический фильтр: если card.placedTurn === state.currentTurn,
  // а кард ранее был воскрешён, флаг livlifeWasResurrected установим в hook.
  // На текущей итерации трактуем любой ally_summoned как from_hand.
  // (Резурект-юниты в основном проходят через resurrectUnit, который НЕ
  // зовёт onUnitSummoned — см. resurrect.js. Так что from_hand=true приемлемо.)
  const owner = state.players?.[ownerIdx];
  if (!owner?.field) return;
  for (const row of owner.field) for (const u of row || []) {
    if (!u || u.id !== KOORDINATOR_VITA_ID) continue;
    if (u.current?.silenced) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    if (u === card) continue; // не на сам триггер выставления
    const t = getHealTarget(state, u);
    if (t) healUnit(state, t, 3, u);
  }
});
registerImplementedAbility(KOORDINATOR_VITA_ID, 'trigger');

// ============================================================
// === Группа: DEATHRATTLE                                    ===
// ============================================================

// === Носитель LL-V (livlife_nositel_llv, rare) ===
// Deathrattle: 1 стак Заражения случайному вражескому юниту.
const NOSITEL_LLV_ID = 'livlife_nositel_llv';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead || dead.id !== NOSITEL_LLV_ID) return;
  if (dead.current?.silenced) return;
  const enemyIdx = 1 - payload.ownerIdx;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return;
  const enemies = [];
  for (const row of enemy.field) for (const u of row || []) {
    if (u && (!u.isAlive || u.isAlive())) enemies.push(u);
  }
  if (enemies.length === 0) return;
  const idx = state.rng?.int ? state.rng.int(enemies.length) : Math.floor(Math.random() * enemies.length);
  addInfectedStack(state, enemies[idx], NOSITEL_LLV_ID, 1);
});
registerImplementedAbility(NOSITEL_LLV_ID, 'deathrattle');

// === Тестировщик Mortis (livlife_testirovshchik_mortis, rare) ===
// Deathrattle: добавляет 1 дополнительную копию своей карты в сброс владельца.
const MORTIS_ID = 'livlife_testirovshchik_mortis';
registerOnUnitDiedExtraHandler((state, payload) => {
  const dead = payload?.card;
  if (!dead || dead.id !== MORTIS_ID) return;
  if (dead.current?.silenced) return;
  const owner = state.players?.[payload.ownerIdx];
  if (!owner) return;
  // Создать копию из template и положить в сброс. Card импортирован выше.
  try {
    const copy = new Card(dead.template);
    copy.ownerId = payload.ownerIdx;
    owner.discard.push(copy);
    state.log?.add(`🧪 Тестировщик Mortis: копия добавлена в сброс`);
  } catch (e) {
    console.warn('[Корпы] Mortis deathrattle:', e);
  }
});
registerImplementedAbility(MORTIS_ID, 'deathrattle');

// ============================================================
// === Группа: ORDER                                          ===
// ============================================================

// === Био-террор «Зеро» (livlife_bio_terror_zero, legendary) ===
// Order: все вражеские юниты на выбранной линии получают 3 стака Заражения.
const BIO_ZERO_ID = 'livlife_bio_terror_zero';
export function bioZeroOrder(state, source, opts = {}) {
  if (!source) return { ok: false };
  const line = opts?.chosenLine;
  if (line !== 0 && line !== 1) return { ok: false, reason: 'no_line' };
  const enemyIdx = 1 - source.ownerId;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return { ok: false };
  const row = enemy.field[line] || [];
  let hit = 0;
  for (const u of row) {
    if (!u) continue;
    addInfectedStack(state, u, BIO_ZERO_ID, 3);
    hit++;
  }
  return { ok: true, hit };
}
registerImplementedAbility(BIO_ZERO_ID, 'order');
registerC1Order(BIO_ZERO_ID, bioZeroOrder);

// ============================================================
// === Группа: AURA                                           ===
// ============================================================

// === Капсула регенерации (livlife_kapsula_regeneratsii, rare) ===
// Аура: все союзные юниты в руке получают +0/0/2 при следующем выставлении.
// Реализация: пока Капсула на поле, помечаем юниты в руке Лив Лайфа флагом
// `kapsulaRegeneratsiiPending`. При выставлении юнита (через
// registerUnitSummonedExtraHandler) — если флаг есть, выдаём +0/0/2 perm и
// снимаем флаг с этой карты.
const KAPSULA_REG_ID = 'livlife_kapsula_regeneratsii';

// 1) При появлении/смерти юнитов или Героя — пересчёт флагов.
function recomputeKapsulaRegFlags(state) {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p) continue;
    // Жива ли хотя бы одна Капсула регенерации у этого игрока и не под Молчанием?
    let hasActiveKapsula = false;
    if (p.field) {
      for (const row of p.field) for (const u of row || []) {
        if (u && u.id === KAPSULA_REG_ID && (!u.isAlive || u.isAlive()) && !u.current?.silenced) {
          hasActiveKapsula = true;
          break;
        }
      }
    }
    // Проставить или снять флаг у юнитов в руке.
    for (const card of (p.hand || [])) {
      if (card?.type !== 'unit') continue;
      card.kapsulaRegeneratsiiPending = hasActiveKapsula;
    }
  }
}
registerUnitSummonedExtraHandler((state, ownerIdx, card) => {
  // На выставление юнита из руки: если флаг был — выдать +0/0/2.
  if (card?.kapsulaRegeneratsiiPending) {
    applyPermanentBuff(state, card, { atk: 0, armor: 0, hp: 2 }, KAPSULA_REG_ID, 'Капсула регенерации');
    card.kapsulaRegeneratsiiPending = false;
  }
  recomputeKapsulaRegFlags(state);
});
registerOnUnitDiedExtraHandler((state) => recomputeKapsulaRegFlags(state));
registerImplementedAbility(KAPSULA_REG_ID, 'aura');

// === Поле регенерации (livlife_pole_regeneratsii, epic) ===
// Аура: все союзники на той же линии получают +0/2/2 (perm).
// Реализация: применяем как permanent buff с sourceId-префиксом, пересчёт
// при изменении состава поля.
const POLE_REG_ID = 'livlife_pole_regeneratsii';
const POLE_REG_SOURCE = `${POLE_REG_ID}:aura`;

function recomputePoleRegeneratsii(state) {
  if (!state?.players) return;
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    // Снять все наши предыдущие баффы.
    for (const row of p.field) for (const u of row || []) {
      if (!u) continue;
      removePermanentBuffsBySource(state, u, (id) => typeof id === 'string' && id.startsWith(POLE_REG_SOURCE));
    }
    // Применить заново.
    for (let line = 0; line < 2; line++) {
      const row = p.field[line];
      if (!row) continue;
      // Есть ли активное Поле на этой линии?
      let active = false;
      for (const u of row) {
        if (u && u.id === POLE_REG_ID && u.isAlive && u.isAlive() && !u.current?.silenced) {
          active = true; break;
        }
      }
      if (!active) continue;
      for (const u of row) {
        if (!u || !u.isAlive || !u.isAlive()) continue;
        if (u.id === POLE_REG_ID) continue; // самому себе не даём (аура исключает источник)
        applyPermanentBuff(state, u, { atk: 0, armor: 2, hp: 2 }, POLE_REG_SOURCE, 'Поле регенерации');
      }
    }
  }
}
registerUnitSummonedExtraHandler((state) => recomputePoleRegeneratsii(state));
registerOnUnitDiedExtraHandler((state) => recomputePoleRegeneratsii(state));
registerImplementedAbility(POLE_REG_ID, 'aura');

// === Куратор «Пандемия» (livlife_kurator_pandemia, epic) ===
// Аура: в начале вражеского хода каждый заражённый враг получает +1 стак.
// Реализация: общий EOT нашего игрока = начало хода врага. Альтернатива —
// SOT, но SOT срабатывает для обоих игроков. Возьмём подход через
// SOT-trigger registered для самого Куратора: «когда turn_start у врага» —
// т.е. трактуем «turn_start у Куратора» как «нашего» и инвертируем filter.
// На текущей итерации проще: каждый SOT — для всех Кураторов на поле,
// если ОВЛАДЕЛЕЦ Куратора != текущий ходящий, то +1 стак вражеским
// заражённым (=своим заражённым).
const KURATOR_PANDEMIA_ID = 'livlife_kurator_pandemia';
registerOnStartOfTurnTrigger(KURATOR_PANDEMIA_ID, (state, source, ownerIdx) => {
  if (!source || source.id !== KURATOR_PANDEMIA_ID) return;
  if (source.current?.silenced) return;
  // ownerIdx — игрок, чей сейчас turn_start. Аура работает «в начале ВРАЖЕСКОГО хода»,
  // т.е. срабатывает когда сейчас ходит враг (ownerIdx != owner Куратора).
  if (ownerIdx === source.ownerId) return;
  // Все заражённые юниты у того кто сейчас ходит (= враги Куратора с этой стороны).
  const target = state.players?.[ownerIdx];
  if (!target?.field) return;
  for (const row of target.field) for (const u of row || []) {
    if (!u) continue;
    if (!u.isAlive || !u.isAlive()) continue;
    const stacks = u.infectedStacks || 0;
    if (stacks <= 0 || stacks >= 3) continue;
    addInfectedStack(state, u, KURATOR_PANDEMIA_ID, 1);
  }
});
registerImplementedAbility(KURATOR_PANDEMIA_ID, 'aura');

// ============================================================
// === Группа: vanilla (deathrattle_contract и т.п.)          ===
// ============================================================

// === Подопытный X-7 (livlife_podopytny_x7, common) ===
// Vanilla с keyword deathrattle_contract в шаблоне — обрабатывается общим
// posthumous_contract.js. Регистрируем как «реализован».
registerImplementedAbility('livlife_podopytny_x7', 'passive');

// === Био-страж LL-3 (taunt) / Парамедик LL-2 / Аугмент-стрелок (vanilla) ===
// Все без abilities, регистрируем чтобы убрать warn.
registerImplementedAbility('livlife_bio_strazh_ll3', 'passive');
registerImplementedAbility('livlife_paramedik_ll2', 'passive');
registerImplementedAbility('livlife_augment_strelok', 'passive');
registerImplementedAbility('livlife_dron_lazaro', 'passive');
